import React, { useMemo } from 'react';
import { Card, Table, Badge, ProgressBar, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from '../../common/Flex';

const RecentOrders = ({ orders, assignments, clients }) => {
  const recentOrdersData = useMemo(() => {
    return orders
      .map(order => {
        const client = clients.find(c => c.ClientID === order.clientId);
        const orderAssignments = assignments.filter(a => a.insertionOrderId === order.id);
        
        const totalAssigned = orderAssignments.reduce((sum, assignment) => {
          let amount = parseFloat(assignment.montoValor) || 0;
          if (assignment.amountType === 'percentage') {
            amount = (amount * parseFloat(order.netInvestment)) / 100;
          }
          return sum + amount;
        }, 0);

        const utilization = order.netInvestment > 0 ? (totalAssigned / order.netInvestment) * 100 : 0;
        const startDate = new Date(order.dateFrom);
        const endDate = new Date(order.dateTo);
        const now = new Date();

        // Calcular estado
        let status = 'pending';
        if (now < startDate) status = 'upcoming';
        else if (now > endDate) status = 'completed';
        else status = 'active';

        // Calcular progreso temporal
        const totalDuration = endDate.getTime() - startDate.getTime();
        const elapsedTime = Math.max(0, now.getTime() - startDate.getTime());
        const timeProgress = totalDuration > 0 ? Math.min(100, (elapsedTime / totalDuration) * 100) : 0;

        return {
          ...order,
          clientName: client ? client.ClientName : 'Cliente Desconocido',
          totalAssigned,
          utilization,
          status,
          timeProgress,
          assignmentsCount: orderAssignments.length,
          daysRemaining: Math.max(0, Math.ceil((endDate - now) / (1000 * 60 * 60 * 24)))
        };
      })
      .sort((a, b) => new Date(b.createdAt || b.dateFrom) - new Date(a.createdAt || a.dateFrom))
      .slice(0, 15); // Mostrar las 15 órdenes más recientes
  }, [orders, assignments, clients]);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return { bg: 'success', text: 'Activa', icon: 'play-circle' };
      case 'completed':
        return { bg: 'secondary', text: 'Completada', icon: 'check-circle' };
      case 'upcoming':
        return { bg: 'info', text: 'Próxima', icon: 'clock' };
      default:
        return { bg: 'warning', text: 'Pendiente', icon: 'pause-circle' };
    }
  };

  const getUtilizationColor = (utilization) => {
    if (utilization > 100) return 'danger';
    if (utilization > 90) return 'warning';
    if (utilization > 70) return 'success';
    return 'info';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: '2-digit'
    });
  };

  return (
    <Card>
      <Card.Header>
        <Flex justifyContent="between" alignItems="center">
          <h6 className="mb-0">Órdenes Recientes</h6>
          <div className="d-flex align-items-center gap-2">
            <Badge bg="primary" className="fs-11">{recentOrdersData.length} órdenes</Badge>
            <FontAwesomeIcon icon="table" className="text-muted" />
          </div>
        </Flex>
      </Card.Header>
      <Card.Body className="p-0">
        {recentOrdersData.length === 0 ? (
          <div className="text-center py-5">
            <FontAwesomeIcon icon="inbox" className="text-muted fs-2 mb-3" />
            <h6 className="text-muted">No hay órdenes disponibles</h6>
            <p className="text-muted mb-0">Las órdenes aparecerán aquí cuando se creen</p>
          </div>
        ) : (
          <div className="table-responsive">
            <Table className="mb-0" hover>
              <thead className="bg-light">
                <tr>
                  <th className="border-0 fs-11 fw-semibold text-700 ps-3">Orden</th>
                  <th className="border-0 fs-11 fw-semibold text-700">Cliente</th>
                  <th className="border-0 fs-11 fw-semibold text-700">Estado</th>
                  <th className="border-0 fs-11 fw-semibold text-700">Presupuesto</th>
                  <th className="border-0 fs-11 fw-semibold text-700">Utilización</th>
                  <th className="border-0 fs-11 fw-semibold text-700">Progreso</th>
                  <th className="border-0 fs-11 fw-semibold text-700">Fechas</th>
                  <th className="border-0 fs-11 fw-semibold text-700 pe-3">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {recentOrdersData.map((order) => {
                  const statusBadge = getStatusBadge(order.status);
                  const utilizationColor = getUtilizationColor(order.utilization);
                  
                  return (
                    <tr key={order.id}>
                      <td className="ps-3 py-3">
                        <div>
                          <h6 className="mb-1 fs-11 fw-semibold">{order.orderName}</h6>
                          <small className="text-muted">{order.businessUnit}</small>
                        </div>
                      </td>
                      
                      <td className="py-3">
                        <div className="fs-11">
                          <div className="fw-semibold">{order.clientName}</div>
                          <small className="text-muted">{order.assignmentsCount} asignaciones</small>
                        </div>
                      </td>
                      
                      <td className="py-3">
                        <Badge bg={statusBadge.bg} className="fs-11">
                          <FontAwesomeIcon icon={statusBadge.icon} className="me-1" />
                          {statusBadge.text}
                        </Badge>
                        {order.status === 'active' && order.daysRemaining <= 7 && (
                          <div className="mt-1">
                            <small className="text-warning">
                              {order.daysRemaining} días restantes
                            </small>
                          </div>
                        )}
                      </td>
                      
                      <td className="py-3">
                        <div className="fs-11">
                          <div className="fw-semibold">{formatCurrency(order.netInvestment)}</div>
                          <small className="text-muted">
                            {formatCurrency(order.totalAssigned)} asignado
                          </small>
                        </div>
                      </td>
                      
                      <td className="py-3">
                        <div className="d-flex align-items-center">
                          <div className="flex-1 me-2">
                            <ProgressBar
                              now={Math.min(100, order.utilization)}
                              variant={utilizationColor}
                              style={{ height: '6px' }}
                              className="mb-1"
                            />
                            <small className={`text-${utilizationColor} fw-semibold`}>
                              {order.utilization.toFixed(1)}%
                            </small>
                          </div>
                        </div>
                      </td>
                      
                      <td className="py-3">
                        {order.status === 'active' && (
                          <div>
                            <ProgressBar
                              now={order.timeProgress}
                              variant="info"
                              style={{ height: '4px' }}
                              className="mb-1"
                            />
                            <small className="text-muted fs-11">
                              {order.timeProgress.toFixed(0)}% tiempo
                            </small>
                          </div>
                        )}
                        {order.status === 'upcoming' && (
                          <small className="text-info">Inicia {formatDate(order.dateFrom)}</small>
                        )}
                        {order.status === 'completed' && (
                          <small className="text-muted">Finalizó {formatDate(order.dateTo)}</small>
                        )}
                      </td>
                      
                      <td className="py-3">
                        <div className="fs-11 text-muted">
                          <div>{formatDate(order.dateFrom)}</div>
                          <div>{formatDate(order.dateTo)}</div>
                        </div>
                      </td>
                      
                      <td className="py-3 pe-3">
                        <div className="d-flex gap-1">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="fs-11 px-2 py-1"
                            title="Ver detalles"
                          >
                            <FontAwesomeIcon icon="eye" />
                          </Button>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="fs-11 px-2 py-1"
                            title="Editar"
                          >
                            <FontAwesomeIcon icon="edit" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default RecentOrders; 